import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import moment from 'moment-timezone';

import TagReminderService from '../../../../services/TagReminderService';

const createResult = (key = null) => ({
  key,
  reminders: [],
  hasData: false,
  loading: false,
  error: false,
});

const usePlannerReminders = ({
  barn,
  locationId,
  barnHorses,
  timezone,
  enabled,
  refreshKey,
  paused = false,
}) => {
  const [now, setNow] = useState(() => new Date());
  const [revision, setRevision] = useState(0);
  const [result, setResult] = useState(createResult);
  const zone = moment.tz.zone(timezone || '')
    ? timezone
    : moment.tz.guess();
  const day = moment(now).tz(zone).format('YYYY-MM-DD');
  const tagIds = [...new Set((barnHorses || []).filter((horse) => !horse.inactive)
    .map((horse) => Number(horse.pingumTagId)).filter((id) => Number.isSafeInteger(id) && id > 0))].sort((left, right) => left - right);
  const tagKey = JSON.stringify(tagIds);
  const key = JSON.stringify([barn?.id, barn?.tenant, locationId, tagKey, zone, day]);
  const requestKey = JSON.stringify([key, refreshKey, revision, enabled, paused]);
  const currentRequestKey = useRef(requestKey);
  currentRequestKey.current = requestKey;
  const canLoad = enabled && !!barn?.tenant && !!locationId && tagIds.length > 0;
  const refresh = useCallback(() => {
    setNow(new Date());
    setRevision((value) => value + 1);
  }, []);

  useEffect(() => {
    const updateClock = () => setNow(new Date());
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    const interval = window.setInterval(updateClock, 60000);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', refresh);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', refresh);
    };
  }, [refresh]);

  useEffect(() => {
    const current = moment(now).tz(zone);
    let boundary = current.clone().startOf('day').hour(17);
    if (current.isSameOrAfter(boundary)) boundary = current.clone().add(1, 'day').startOf('day');
    const timer = window.setTimeout(() => setNow(new Date()), Math.max(1, boundary.valueOf() - new Date().getTime()));
    return () => window.clearTimeout(timer);
  }, [now, zone]);

  useEffect(() => {
    if (!canLoad || paused) {
      setResult((previous) => {
        if (!canLoad || previous.key !== key) return createResult(key);
        if (!previous.loading) return previous;
        return {
          ...previous,
          loading: false,
        };
      });
      return undefined;
    }
    let active = true;
    setResult((previous) => {
      const current = previous.key === key
        ? previous
        : createResult(key);
      return {
        ...current,
        loading: true,
        error: false,
      };
    });
    TagReminderService.getAllTagReminders({ tagIds: JSON.parse(tagKey) }).then((reminders) => {
      if (!active || currentRequestKey.current !== requestKey) return;
      if (!Array.isArray(reminders)) throw new Error('Unexpected reminder response');
      setResult({
        key,
        reminders,
        hasData: true,
        loading: false,
        error: false,
      });
    }).catch(() => {
      if (!active || currentRequestKey.current !== requestKey) return;
      setResult((previous) => ({
        ...previous,
        loading: false,
        error: true,
      }));
    });
    return () => { active = false; };
  }, [key, canLoad, tagKey, requestKey, paused]);

  const current = result.key === key;
  const hasData = current && result.hasData;
  const fetching = canLoad && !paused && (!current || result.loading);
  return {
    now,
    reminders: current && canLoad
      ? result.reminders
      : [],
    loading: fetching && !hasData,
    refreshing: fetching && hasData,
    error: canLoad && current && result.error,
    refresh,
  };
};

export default usePlannerReminders;
